import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAnnee } from '../hooks/useAnnee';
import { useRessource } from '../hooks/useRessource';
import { api } from '../lib/apiClient';
import { Badge, Chargement, EnTetePage, Erreur, Infobulle } from '../components/Elements';
import { IconeCivilite } from '../components/IconeCivilite';
import { PointEntite, TagEntite } from '../components/TagEntite';
import { IconeMutuelle, IconeTransport } from '../components/IconesContrat';
import { FormulairePersonnel } from './ReferentielsPages';
import { FormulaireContrat } from './ContratsPage';
import { ResumePaie, TableauElementsPaie } from '../components/ElementsPaie';
import { moisPortes } from '../lib/paie';
import { libelleMotifFin, libelleStatutContrat, libelleTitreTransport } from '../lib/contrat';
import {
  formaterDate,
  formaterDateHeure,
  formaterDuree,
  formaterHeure,
  formaterMois,
  libelleJour,
} from '../lib/format';
import type {
  CalculPaie,
  Contrat,
  Creneau,
  DocumentsPersonnel,
  FichierDrive,
  Personnel,
  TypeDocumentsPersonnel,
} from '../types';
import { formaterTelephone } from '../lib/telephone';

/**
 * Fiche detaillee d'un membre du personnel, sur le modele de la fiche
 * adherent : une identite en tete, puis des sections qui repondent
 * chacune a une question — pour qui travaille-t-il, que fait-il, que
 * faut-il pour le payer.
 *
 * TROIS NIVEAUX DE VISIBILITE cohabitent ici, et c'est le serveur qui
 * tranche, pas l'ecran :
 *   - l'identite, visible de tout titulaire de `personnel.lire` ;
 *   - les contrats, reserves a `contrat.lire` (Direction) et journalises ;
 *   - l'etat civil et le paiement, servis dans la meme permission — la
 *     fiche arrive EPUREE pour les autres, champs a null.
 * L'ecran ne fait donc que refleter ce qu'il a recu.
 */
export function PersonnelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const naviguer = useNavigate();
  const { peut, entiteActive } = useAuth();
  const [modale, setModale] = useState(false);
  const [contratOuvert, setContratOuvert] = useState<Contrat | null>(null);
  const [nouveauContrat, setNouveauContrat] = useState(false);
  // Le mois regarde dans le bloc Elements de paie ; null = pas encore
  // choisi, le premier mois ouvert fait foi.
  const [moisPaie, setMoisPaie] = useState<string | null>(null);

  const peutContrats = peut('contrat.lire');
  const cle = entiteActive?.id ?? '';

  const fiche = useRessource(() => api.get<Personnel>(`/api/personnel/${id}`), [id, cle]);

  // Contrats de TOUTES les entites habilitees : quelqu'un peut enseigner
  // dans les deux ecoles, avec un statut different de part et d'autre.
  const contrats = useRessource(
    () =>
      peutContrats
        ? api.get<Contrat[]>('/api/contrats', { personnelId: id, toutesEntites: true })
        : Promise.resolve([]),
    [id, peutContrats, cle],
  );

  // Creneaux de TOUTES les entites habilitees (2026-10-03), comme les
  // contrats : Laura enseigne a Artistic et a Domusic, sa fiche montre
  // les deux, chaque creneau a la pastille de son ecole.
  const annee = useAnnee();
  const creneaux = useRessource(
    () =>
      annee.anneeId
        ? api.get<Creneau[]>('/api/creneaux', { anneeId: annee.anneeId, personnelId: id, toutesEntites: true })
        : Promise.resolve([]),
    [id, annee.anneeId, cle],
  );

  // Les elements de paie de l'ECOLE ACTIVE (2026-10-03) : le calcul est
  // cloisonne, et la saisie d'un element se fait dans l'ecran Paie de
  // l'ecole. La reponse est reduite a cette personne par le serveur.
  const paie = useRessource(
    () =>
      peutContrats && annee.anneeId
        ? api.get<CalculPaie>('/api/paie', { anneeId: annee.anneeId, personnelId: id })
        : Promise.resolve(null),
    [id, annee.anneeId, cle, peutContrats],
  );

  if (fiche.chargement) return <Chargement />;
  if (fiche.erreur) return <Erreur message={fiche.erreur} onReessayer={fiche.recharger} />;
  if (!fiche.donnees) return null;

  const personnel = fiche.donnees;
  const tous = contrats.donnees ?? [];
  const enCours = tous.filter((c) => c.en_cours);
  const clos = tous.filter((c) => !c.en_cours);
  // Un ADMINISTRATIF n'encadre pas de cours : le bloc Creneaux n'a pas de
  // sens sur sa fiche (2026-10-03). Le critere est l'ensemble de ses
  // contrats en cours ; sans contrat lisible, le bloc reste.
  const administratif = enCours.length > 0 && enCours.every((c) => c.statut === 'administratif');

  // Le bloc contrat se GRISE quand plus rien n'est en cours : la fiche
  // reste consultable — on a besoin de l'historique d'un professeur
  // parti — mais l'ecran dit d'un coup d'oeil qu'il n'est plus employe.
  const sansContrat = enCours.length === 0;

  // Le titre du bloc Contrats porte l'ANCIENNETE A DATE (demande de
  // Georges du 2026-10-30) : les mois depuis le premier contrat dans
  // l'ecole et les points acquis au mois courant — 24 mois avant les
  // premiers. Employee par les deux ecoles, une personne a une
  // anciennete par ecole : une mention par ecole. La mention « a date »
  // est retiree le 2026-11-04 (Georges) : le titre dit deja l'etat du jour.
  const parEcole = [...new Map(enCours.map((c) => [c.entite_code, c])).values()];
  const mention = (c: Contrat) =>
    `${c.anciennete_mois} mois d’ancienneté · ${c.points_anciennete} ${
      c.points_anciennete === 1 ? 'point' : 'points'
    }`;
  const titreContrats =
    parEcole.length === 0
      ? 'Contrats'
      : parEcole.length === 1
        ? `Contrats (${mention(parEcole[0]!)})`
        : `Contrats (${parEcole.map((c) => `${c.entite_code} : ${mention(c)}`).join(' ; ')})`;

  // La ligne d'un contrat ouvre sa fiche (2026-10-03) — la modale de
  // l'ecran Contrats, telle quelle. Seulement pour l'ECOLE ACTIVE : la
  // table est cloisonnee, et l'enregistrement d'un contrat de l'autre
  // ecole serait refuse par le serveur. On bascule d'ecole pour y toucher.
  const contratModifiable = (contrat: Contrat) =>
    peut('contrat.gerer') && contrat.entite_id === entiteActive?.id;

  return (
    <>
      <button
        type="button"
        className="mb-4 text-sm text-encre-500 underline"
        onClick={() => naviguer('/personnel')}
      >
        ← Retour au personnel
      </button>

      <EnTetePage
        titre={
          <>
            <IconeCivilite civilite={personnel.civilite} />
            {personnel.nom ? `${personnel.nom.toUpperCase()} ` : ''}
            {personnel.prenom_usuel}
            {/* L'entite employeuse se lit a cote du nom : c'est la
                premiere chose qu'on cherche sur un referentiel partage
                entre deux ecoles. */}
            {enCours.map((contrat) => (
              <span key={contrat.id} className="inline-flex items-center gap-1">
                <TagEntite code={contrat.entite_code} couleur={contrat.entite_couleur} />
                <Badge>{contrat.type.toUpperCase()}</Badge>
              </span>
            ))}
          </>
        }
        description={
          peutContrats && sansContrat
            ? 'Aucun contrat en cours — la fiche reste consultable, son historique aussi.'
            : 'Le prénom est celui du planning ; le nom légal vient du tableau des salaires.'
        }
        actions={
          peut('personnel.modifier') ? (
            <button type="button" className="bouton-secondaire" onClick={() => setModale(true)}>
              Modifier la fiche
            </button>
          ) : null
        }
      />

      {(personnel.email || personnel.telephone) && (
        <div className="carte mb-6 flex flex-wrap gap-x-8 gap-y-2 p-4 text-sm">
          {personnel.email && (
            <div>
              <span className="text-xs text-encre-400">Email</span>
              <div>{personnel.email}</div>
            </div>
          )}
          {personnel.telephone && (
            <div>
              <span className="text-xs text-encre-400">Téléphone</span>
              <div className="tabulaire">
                {formaterTelephone(personnel.telephone, personnel.telephone_pays)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* --- Contrats --------------------------------------------------- */}
      {peutContrats && (
        <Section
          cle="contrats"
          titre={titreContrats}
          grise={sansContrat}
          actions={
            // Un nouveau contrat, dans l'ECOLE ACTIVE, pour cette fiche
            // (2026-10-03). Le serveur refuse un second contrat ouvert sur
            // le meme couple salarie-ecole et le dit.
            peut('contrat.gerer') ? (
              <button type="button" className="bouton-secondaire" onClick={() => setNouveauContrat(true)}>
                Ajouter
              </button>
            ) : null
          }
        >
          {tous.length === 0 ? (
            <p className="px-4 py-3 text-sm text-encre-500">
              Aucun contrat. Cette fiche n’est rattachée à aucune école — c’est le cas d’un
              intervenant repris du classeur des cours dont l’embauche reste à saisir.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-encre-200 text-left text-xs uppercase tracking-wide text-encre-500">
                    <th className="px-4 py-2">Type</th>
                    <th className="px-4 py-2">Statut</th>
                    <th className="px-4 py-2">Période</th>
                    <th className="px-4 py-2">Mutuelle</th>
                    <th className="px-4 py-2">Transport</th>
                  </tr>
                </thead>
                <tbody>
                  {[...enCours, ...clos].map((contrat) => (
                    <tr
                      key={contrat.id}
                      className={`border-b border-encre-100 ${contrat.en_cours ? '' : 'text-encre-400'} ${
                        contratModifiable(contrat) ? 'cursor-pointer hover:bg-encre-50' : ''
                      }`}
                      onClick={contratModifiable(contrat) ? () => setContratOuvert(contrat) : undefined}
                    >
                      <td className="px-4 py-2">
                        {/* Le point de couleur dit l'ecole ; un contrat de
                            l'autre ecole dit, au survol, qu'il faut basculer. */}
                        <Infobulle
                          texte={
                            peut('contrat.gerer') && !contratModifiable(contrat)
                              ? `Contrat ${contrat.entite_code} : basculez sur cette école pour le modifier`
                              : null
                          }
                          className="inline-flex items-center gap-1.5"
                        >
                          <PointEntite code={contrat.entite_code} couleur={contrat.entite_couleur} />
                          {contrat.type.toUpperCase()}
                        </Infobulle>
                      </td>
                      <td className="px-4 py-2">{libelleStatutContrat(contrat.statut)}</td>
                      <td className="px-4 py-2 tabulaire">
                        {formaterDate(contrat.date_debut)}
                        {contrat.date_fin ? ` → ${formaterDate(contrat.date_fin)}` : ' → en cours'}
                        {contrat.motif_fin && (
                          <span className="block text-xs text-encre-500">
                            {libelleMotifFin(contrat.motif_fin)}
                          </span>
                        )}
                      </td>
                      {/* Mutuelle et transport en colonnes (2026-10-30) :
                          l'icone de l'ecran Contrats, puis le detail en
                          clair — date de souscription, titre et part
                          remboursee. */}
                      <td className="px-4 py-2">
                        {contrat.mutuelle && (
                          <span className="inline-flex items-center gap-1.5">
                            <IconeMutuelle depuis={contrat.date_souscription_mutuelle} />
                            {contrat.date_souscription_mutuelle && (
                              <span className="text-xs text-encre-500 tabulaire">
                                depuis le {formaterDate(contrat.date_souscription_mutuelle)}
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {/* Le TYPE du titre, pas son prix (Georges, 2026-11-04) :
                            le montant reste dans l'infobulle de l'icone. */}
                        {(contrat.titre_transport ?? 0) > 0 && (
                          <span className="inline-flex items-center gap-1.5">
                            <IconeTransport montant={contrat.titre_transport!} type={contrat.titre_transport_type} />
                            <span>{libelleTitreTransport(contrat.titre_transport_type)}</span>
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      )}

      {/* --- Créneaux ---------------------------------------------------- */}
      {!administratif && (
      <Section
        cle="creneaux"
        titre={`Créneaux ${annee.annee?.libelle ?? ''}`}
        description="Ce que cette personne encadre, sur l’année en cours, dans chaque école où vous êtes habilité."
      >
        {(creneaux.donnees ?? []).length === 0 ? (
          <p className="px-4 py-3 text-sm text-encre-500">Aucun créneau sur cette année.</p>
        ) : (
          <ul className="divide-y divide-encre-100">
            {(creneaux.donnees ?? []).map((creneau) => (
              <li key={creneau.id} className="flex flex-wrap items-baseline gap-x-3 px-4 py-2 text-sm">
                <span className="tabulaire flex w-44 shrink-0 items-center gap-2">
                  {creneau.entite_code && creneau.entite_couleur && (
                    <PointEntite code={creneau.entite_code} couleur={creneau.entite_couleur} />
                  )}
                  {libelleJour(creneau.jour)} {formaterHeure(creneau.heure_debut)}
                </span>
                <span className="font-medium">{creneau.activite_libelle}</span>
                <span className="text-encre-500">{formaterDuree(creneau.duree_minutes)}</span>
                {creneau.lieu_code && <span className="text-encre-400">{creneau.lieu_code}</span>}
                <span className="ml-auto tabulaire text-encre-500">
                  {creneau.effectif}
                  {creneau.capacite_max !== null && `/${creneau.capacite_max}`} inscrit(s)
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
      )}

      {/* --- Éléments de paie ------------------------------------------- */}
      {peutContrats && <BlocElementsPaie paie={paie.donnees ?? null} mois={moisPaie} onMois={setMoisPaie} />}

      {/* --- Documents ---------------------------------------------------- */}
      {peutContrats && <BlocDocuments personnelId={personnel.id} version={cle} />}

      {/* --- État civil et paiement -------------------------------------- */}
      {peutContrats && (
        <Section cle="etat-civil" titre="État civil et paiement">
          <dl className="grid gap-x-8 gap-y-3 px-4 py-3 text-sm sm:grid-cols-3">
            <Donnee libelle="Date de naissance" valeur={formaterDate(personnel.date_naissance)} />
            <Donnee libelle="Lieu de naissance" valeur={personnel.lieu_naissance} />
            <Donnee libelle="N° de Sécurité sociale" valeur={personnel.numero_securite_sociale} />
            <Donnee
              libelle="Adresse"
              valeur={[personnel.adresse, personnel.code_postal, personnel.ville]
                .filter(Boolean)
                .join(' ')}
            />
            <Donnee libelle="IBAN" valeur={personnel.iban} />
            <Donnee libelle="BIC" valeur={personnel.bic} />
            <Donnee libelle="N° GUSO" valeur={personnel.numero_guso} />
          </dl>
        </Section>
      )}

      {(contratOuvert || nouveauContrat) && (
        <FormulaireContrat
          personnel={[personnel]}
          contrat={contratOuvert}
          personnelImpose={contratOuvert ? null : personnel}
          onFermer={() => {
            setContratOuvert(null);
            setNouveauContrat(false);
          }}
          onEnregistre={() => {
            setContratOuvert(null);
            setNouveauContrat(false);
            contrats.recharger();
          }}
        />
      )}

      <FormulairePersonnel
        etat={{ ouverte: modale, personnel }}
        onFermer={() => setModale(false)}
        onEnregistre={() => {
          setModale(false);
          fiche.recharger();
        }}
      />
    </>
  );
}

/**
 * Les elements de paie du salarie, sur un mois, dans l'ecole active : le
 * tableau de la modale « Element de paie » de l'ecran Paie, en
 * consultation. Le regime general d'abord ; le cachet suit s'il en porte.
 * Le selecteur de mois est dans le bandeau du bloc.
 */
function BlocElementsPaie({
  paie,
  mois,
  onMois,
}: {
  paie: CalculPaie | null;
  mois: string | null;
  onMois: (mois: string) => void;
}) {
  const { entiteActive } = useAuth();
  const personnel = paie?.personnel[0] ?? null;
  const titre = `Éléments de paie ${paie?.annee_libelle ?? ''}`;
  if (!paie || !personnel) {
    return (
      <Section cle="paie" titre={titre}>
        <p className="px-4 py-3 text-sm text-encre-500">
          Aucun élément de paie dans {entiteActive?.code ?? 'cette école'} sur cette année.
        </p>
      </Section>
    );
  }
  const clotures = new Set(paie.mois_clotures);
  const portes = moisPortes(personnel, 'general');
  const portesCachet = moisPortes(personnel, 'cachet');
  const tous = [...new Set([...portes, ...portesCachet])].sort();
  // Comme la modale : le premier mois OUVERT, a defaut le dernier porte.
  const moisAffiche =
    mois && tous.includes(mois) ? mois : (tous.find((m) => !clotures.has(m)) ?? tous[tous.length - 1] ?? '');

  return (
    <Section
      cle="paie"
      titre={titre}
      actions={
        tous.length > 0 ? (
          <span className="flex items-center gap-2">
            <label className="text-xs text-encre-500" htmlFor="mois-paie-fiche">
              Mois
            </label>
            <select
              id="mois-paie-fiche"
              className="champ py-1 text-sm"
              value={moisAffiche}
              onChange={(e) => onMois(e.target.value)}
            >
              {tous.map((m) => (
                <option key={m} value={m}>
                  {clotures.has(m) ? '🔒 ' : ''}
                  {formaterMois(m)}
                </option>
              ))}
            </select>
          </span>
        ) : null
      }
    >
      <div className="px-4 py-3">
        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-encre-600">
          <ResumePaie personnel={personnel} mode="general" mois={paie.mois} moisFiltre={moisAffiche} />
        </div>
        <TableauElementsPaie
          personnel={personnel}
          mode="general"
          moisFiltre={moisAffiche}
          moisClotures={paie.mois_clotures}
        />
        {portesCachet.length > 0 && (
          <>
            <h3 className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-encre-500">Cachet</h3>
            <TableauElementsPaie
              personnel={personnel}
              mode="cachet"
              moisFiltre={moisAffiche}
              moisClotures={paie.mois_clotures}
            />
          </>
        )}
      </div>
    </Section>
  );
}

/** Une taille de fichier lisible : octets, Ko, Mo. */
function formaterTaille(octets: number | null): string {
  if (octets === null) return '';
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} Ko`;
  return `${(octets / (1024 * 1024)).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mo`;
}

/**
 * Les documents du salarie, lus sur le Drive : pour chaque ecole ou il a
 * un contrat, chaque type de document a {salarie} et ce que son dossier
 * contient. Les fichiers sont deposes par un processus EXTERNE — le bloc
 * ne depose rien, il montre. Le lien ouvre le fichier dans le Drive, il
 * faut en etre membre.
 *
 * Chaque type se replie, et dans un type chaque sous-dossier (une annee,
 * le plus souvent) aussi : vingt fiches de paie sur trois ans ne doivent
 * pas allonger la fiche (2026-10-04).
 *
 * LE DRIVE NE SE LIT QUE BLOC OUVERT (2026-11-04) : le contenu n'existe que
 * si la section le rend, et c'est lui qui charge. Un bloc replie ne coute
 * aucun appel a Google, et la lecture reprend a sa reouverture.
 */
function BlocDocuments({ personnelId, version }: { personnelId: string; version: unknown }) {
  return (
    <Section
      cle="documents"
      titre="Documents"
      description="Ce que le Drive de chaque école porte dans le dossier de cette personne."
    >
      <ContenuDocuments personnelId={personnelId} version={version} />
    </Section>
  );
}

function ContenuDocuments({ personnelId, version }: { personnelId: string; version: unknown }) {
  const { donnees: documents, chargement, erreur } = useRessource(
    () => api.get<DocumentsPersonnel>(`/api/personnel/${personnelId}/documents`),
    [personnelId, version],
  );
  const ecoles = documents?.ecoles ?? [];
  const plusieurs = ecoles.length > 1;
  return (
    <>
      {chargement && !documents ? (
        <p className="px-4 py-3 text-sm text-encre-400">Lecture du Drive…</p>
      ) : erreur ? (
        <p className="px-4 py-3 text-sm text-red-700">{erreur}</p>
      ) : !documents || ecoles.length === 0 ? (
        <p className="px-4 py-3 text-sm text-encre-500">Aucune école ne porte de contrat pour cette personne.</p>
      ) : !documents.compte_connecte ? (
        <p className="px-4 py-3 text-sm text-encre-500">
          Aucun compte Google connecté avec l’accès au Drive (Administration → Paramètres) : les documents ne
          peuvent pas être lus.
        </p>
      ) : (
        <div className="divide-y divide-encre-100">
          {ecoles.map((ecole) => (
            <div key={ecole.entite_id} className="px-4 py-3">
              {plusieurs && (
                <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-encre-500">
                  <PointEntite code={ecole.entite_code} couleur={ecole.entite_couleur} />
                  {ecole.entite_code}
                </div>
              )}
              {!ecole.drive_regle ? (
                <p className="text-sm text-encre-500">
                  Aucun Drive partagé renseigné sur la fiche de l’école {ecole.entite_code}.
                </p>
              ) : ecole.erreur ? (
                <p className="text-sm text-red-700">Le Drive de {ecole.entite_code} n’a pas répondu : {ecole.erreur}</p>
              ) : ecole.types.length === 0 ? (
                <p className="text-sm text-encre-500">
                  Aucun type de document rattaché à une personne sur la fiche de l’école {ecole.entite_code} (bloc
                  Documents et archivage, variable {'{salarie}'}).
                </p>
              ) : (
                <div className="space-y-2">
                  {ecole.types.map((type) => (
                    <TypeDocuments key={type.nature} ecole={ecole.entite_code} type={type} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/** Un type de document : sa nature, son dossier, ses fichiers groupes par sous-dossier. */
function TypeDocuments({ ecole, type }: { ecole: string; type: TypeDocumentsPersonnel }) {
  // Les fichiers a la racine du dossier d'abord, puis chaque sous-dossier
  // dans l'ordre de son nom — « 2025-26 » avant « 2026-27 ».
  const dossiers = new Map<string, FichierDrive[]>();
  for (const fichier of type.fichiers) {
    const liste = dossiers.get(fichier.sous_chemin) ?? [];
    liste.push(fichier);
    dossiers.set(fichier.sous_chemin, liste);
  }
  const racine = dossiers.get('') ?? [];
  const sousDossiers = [...dossiers.keys()].filter((d) => d !== '').sort((a, b) => a.localeCompare(b, 'fr'));

  return (
    <Pliable
      cle={`documents:${ecole}:${type.nature}`}
      entete={
        <>
          <span className="font-medium">{type.nature}</span>
          <span className="text-xs text-encre-400">{type.fichiers.length}</span>
        </>
      }
      apres={
        type.dossier_url ? (
          <Infobulle texte="Ouvrir le dossier dans le Drive">
            <a
              href={type.dossier_url}
              target="_blank"
              rel="noreferrer"
              className="tabulaire text-xs text-encre-400 underline"
            >
              {type.chemin}
            </a>
          </Infobulle>
        ) : (
          <span className="tabulaire text-xs text-encre-400">{type.chemin}</span>
        )
      }
    >
      {!type.trouve ? (
        <p className="text-sm text-encre-400">Pas encore de dossier sur le Drive.</p>
      ) : type.fichiers.length === 0 ? (
        <p className="text-sm text-encre-400">Dossier vide.</p>
      ) : (
        <div className="space-y-1.5">
          {racine.length > 0 && <ListeFichiers fichiers={racine} />}
          {sousDossiers.map((dossier) => (
            <Pliable
              key={dossier}
              cle={`documents:${ecole}:${type.nature}:${dossier}`}
              entete={
                <>
                  <IconeDossier />
                  <span className="tabulaire">{dossier}</span>
                  <span className="text-xs text-encre-400">{dossiers.get(dossier)!.length}</span>
                </>
              }
              retrait
            >
              <ListeFichiers fichiers={dossiers.get(dossier)!} />
            </Pliable>
          ))}
        </div>
      )}
    </Pliable>
  );
}

function ListeFichiers({ fichiers }: { fichiers: FichierDrive[] }) {
  return (
    <ul className="divide-y divide-encre-100 rounded-md border border-encre-100">
      {fichiers.map((fichier) => (
        <li key={fichier.id} className="flex flex-wrap items-baseline gap-x-3 px-3 py-1.5 text-sm">
          {fichier.url ? (
            <a href={fichier.url} target="_blank" rel="noreferrer" className="underline">
              {fichier.nom}
            </a>
          ) : (
            <span>{fichier.nom}</span>
          )}
          <span className="ml-auto tabulaire text-xs text-encre-400">
            {formaterTaille(fichier.taille)}
            {fichier.taille !== null && fichier.modifie_le ? ' · ' : ''}
            {fichier.modifie_le ? formaterDateHeure(fichier.modifie_le) : ''}
          </span>
        </li>
      ))}
    </ul>
  );
}

function IconeDossier() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0 text-encre-400" aria-hidden>
      <path
        d="M1.5 3.5A1.5 1.5 0 0 1 3 2h3.2a1.5 1.5 0 0 1 1.06.44L8.5 3.7H13A1.5 1.5 0 0 1 14.5 5.2v7.3A1.5 1.5 0 0 1 13 14H3a1.5 1.5 0 0 1-1.5-1.5v-9z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * L'etat ouvert / replie d'un bloc, PAR POSTE : une commodite de lecture,
 * pas une donnee. Le stockage du navigateur peut manquer (fenetre privee,
 * donnees effacees) : on lit et on ecrit sous garde, et le defaut vaut.
 */
function useRepli(cle: string, defaut = true): [boolean, () => void] {
  const stockage = `fiche-personnel:${cle}`;
  const [ouvert, setOuvert] = useState<boolean>(() => {
    try {
      const lu = window.localStorage.getItem(stockage);
      return lu === null ? defaut : lu === '1';
    } catch {
      return defaut;
    }
  });
  const basculer = () => {
    setOuvert((avant) => {
      try {
        window.localStorage.setItem(stockage, avant ? '0' : '1');
      } catch {
        // Pas de stockage : l'etat ne survit pas au rechargement, c'est tout.
      }
      return !avant;
    });
  };
  return [ouvert, basculer];
}

/** Le chevron qui dit l'etat d'un bloc : vers la droite replie, vers le bas ouvert. */
function Chevron({ ouvert }: { ouvert: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className={`h-3.5 w-3.5 shrink-0 text-encre-400 transition-transform ${ouvert ? 'rotate-90' : ''}`}
      aria-hidden
    >
      <path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Un pli dans un bloc : l'en-tete est un bouton (chevron + libelle), ce
 * qui suit l'en-tete — un lien vers le Drive — reste HORS du bouton, un
 * lien dans un bouton n'etant ni valide ni cliquable proprement.
 */
function Pliable({
  cle,
  entete,
  apres,
  retrait = false,
  children,
}: {
  cle: string;
  entete: React.ReactNode;
  apres?: React.ReactNode;
  /** Un pli de second niveau : decale, en plus petit. */
  retrait?: boolean;
  children: React.ReactNode;
}) {
  const [ouvert, basculer] = useRepli(cle);
  return (
    <div className={retrait ? 'ml-5' : ''}>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <button
          type="button"
          className="flex items-center gap-1.5 rounded py-0.5 text-sm hover:text-encre-900"
          onClick={basculer}
          aria-expanded={ouvert}
        >
          <Chevron ouvert={ouvert} />
          {entete}
        </button>
        {apres}
      </div>
      {ouvert && <div className="mt-1 pl-5">{children}</div>}
    </div>
  );
}

/**
 * Meme carte a en-tete que la fiche adherent, avec un etat grise. Depuis
 * le 2026-10-04, l'en-tete REPLIE le bloc au clic : le titre est un
 * bouton, les actions du bandeau (« Ajouter », le mois) restent a cote,
 * hors du bouton. L'etat est retenu par poste (`useRepli`).
 */
function Section({
  cle,
  titre,
  description,
  grise = false,
  actions,
  children,
}: {
  /** La cle de l'etat ouvert / replie, stable d'une annee a l'autre — pas le titre, qui porte l'annee. */
  cle: string;
  titre: string;
  description?: string;
  grise?: boolean;
  /** A droite du titre, dans le bandeau du bloc. */
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [ouvert, basculer] = useRepli(cle);
  return (
    <section
      className={`carte mb-6 overflow-hidden ${grise ? 'bg-encre-50 text-encre-400 opacity-75' : ''}`}
    >
      <div
        className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 ${
          ouvert ? 'border-b border-encre-200' : ''
        }`}
      >
        <button
          type="button"
          className="flex min-w-0 flex-1 items-start gap-2 text-left"
          onClick={basculer}
          aria-expanded={ouvert}
        >
          <span className="mt-0.5">
            <Chevron ouvert={ouvert} />
          </span>
          <span>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-encre-500">{titre}</h2>
            {description && <p className="mt-0.5 text-xs text-encre-400">{description}</p>}
          </span>
        </button>
        {actions}
      </div>
      {ouvert && children}
    </section>
  );
}

function Donnee({ libelle, valeur }: { libelle: string; valeur: string | null | undefined }) {
  return (
    <div>
      <dt className="text-xs text-encre-400">{libelle}</dt>
      <dd className="tabulaire">
        {valeur && valeur !== '—' ? valeur : <span className="text-encre-300">non renseigné</span>}
      </dd>
    </div>
  );
}
